import styled from "styled-components";
import { tokens } from "../../../public/tokens";

const { colors, typography, globals, effects } = tokens;

export const LinkPopover = styled.div({
    position: "absolute",
    right: 0,
    bottom: "calc(100% + 10px)",
    width: "755px",
    maxWidth: "calc(100vw - 24px)",
    padding: globals["spacing-lg"],
    boxSizing: "border-box",
    background: colors.surfaces["container-background"],
    boxShadow: effects.effects.shadow.default,
    borderRadius: globals["border-radius"],
    borderStyle: "solid",
    borderWidth: "1px",
    borderColor: colors.surfaces["border-divider"],
    zIndex: 1000,
});

export const LinkPopoverContent = styled.div({
    width: "100%",
    display: "flex",
    alignItems: "flex-end",
    gap: globals["spacing-lg"],
});

export const LinkField = styled.div({
    flex: "0 0 auto",
    display: "flex",
    flexDirection: "column",
    gap: globals["spacing-px"],
});

/**
 * Fixed widths match the reference mockup (no Figma file for this popover):
 * 755px content area as Link (255px) + URL (312px) + Insert button.
 */
export const LinkFieldName = styled(LinkField)({
    width: "255px",
});

export const LinkFieldUrl = styled(LinkField)({
    width: "312px",
});

export const LinkLabel = styled.label({
    color: colors.text.primary,
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.regular,
    lineHeight: "20px",
});

export const LinkInputBox = styled.div({
    height: globals["spacing-10"],
    padding: `0 ${globals["spacing-2-5"]}`,
    display: "flex",
    alignItems: "center",
    background: colors.surfaces["container-background"],
    borderStyle: "solid",
    borderWidth: "1px",
    borderColor: colors.surfaces["border-divider"],
    borderRadius: globals["border-radius"],
    boxShadow: effects.effects.shadow.default,
    transition: "border-color 0.15s ease-in-out",

    "&:focus-within": {
        borderColor: colors.brand.blue["500"],
    },

    input: {
        flex: 1,
        minWidth: 0,
        width: "100%",
        padding: 0,
        border: "none",
        outline: "none",
        background: "transparent",
        color: colors.text.primary,
        fontFamily: typography.fontFamilies["albert-sans"],
        fontSize: typography.fontSize["lg-14px"],
        fontWeight: typography.fontWeights.regular,
        lineHeight: "20px",

        "&::placeholder": {
            color: colors.neutral.grey["600"],
            opacity: 1,
        },
    },
});

export const InsertButton = styled.button({
    height: globals["spacing-10"],
    padding: `${globals["spacing-1"]} ${globals["spacing-2"]}`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: globals["spacing-1"],
    border: "none",
    borderRadius: globals["border-radius"],
    background: colors.theme["button-color"],
    color: colors.ui["primary-foreground"],
    fontFamily: typography.fontFamilies["albert-sans"],
    fontSize: typography.fontSize["md-12px"],
    fontWeight: typography.fontWeights.medium,
    lineHeight: "16px",
    cursor: "pointer",
    transition: "background 0.15s ease-in-out",

    "&:hover": {
        background: colors.theme["button-hover-color"],
    },

    "&:active": {
        background: colors.theme["button-active-color"],
    },
});

/**
 * Generic square icon-only button (ghost style) — renders a single
 * FontAwesome `<i>` child and nothing else. Not tied to any one feature;
 * compose it with a positioning wrapper (see PopoverCloseButton) rather
 * than adding new one-off variants.
 * @example <IconButton onClick={onClose}><i className="fal fa-times" /></IconButton>
 */
export const IconButton = styled.button({
    width: globals["spacing-5"],
    height: globals["spacing-5"],
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    borderRadius: globals["border-radius"],
    background: "transparent",
    color: colors.text.secondary,
    cursor: "pointer",

    "&:hover": {
        background: colors.neutral.grey.Alpha["10"],
        color: colors.text.primary,
    },

    i: {
        fontSize: typography.fontSize["lg-14px"],
    },
});

/**
 * IconButton pinned to the top-right corner of a positioned ancestor.
 * Used for the LinkPopover's close control - the only thing that
 * distinguishes it from IconButton is this placement.
 */
export const PopoverCloseButton = styled(IconButton)({
    position: "absolute",
    top: globals["spacing-3"],
    right: globals["spacing-3"],
});
